/**
 * Inkwell — persistent printer context.
 * Owner: Agent B. No dependencies, safe to load twice, safe to load in <head> with defer.
 *
 * Public contract (see docs/AGENT_BRIEF.md section 6):
 *   window.InkwellPrinter.KEY    -> 'inkwell:printer'
 *   window.InkwellPrinter.EVENT  -> 'inkwell:printer-change'
 *   window.InkwellPrinter.get()  -> {handle, displayName, brand, series} | null
 *   window.InkwellPrinter.set(model)
 *   window.InkwellPrinter.clear()
 *   window.InkwellPrinter.OPEN_EVENT -> 'inkwell:open-printer-finder'
 *   window.InkwellPrinter.open()     -> true if a finder was revealed, false otherwise
 *
 * Consumers listen on document:
 *   document.addEventListener('inkwell:printer-change', (e) => e.detail.printer)
 *
 * Consumers ask for the finder by dispatching a cancelable event on document:
 *   const ev = new CustomEvent('inkwell:open-printer-finder', { cancelable: true });
 *   if (document.dispatchEvent(ev)) { location.href = '/#printer-finder'; }
 * This script calls preventDefault() only when it actually found a visible
 * printer-finder on the page, so the caller's fallback still runs otherwise.
 *
 * Also renders the site-wide context bar ("Shopping for: <model>  x") directly
 * after #header-group, so it appears on every page, not only pages that contain
 * the printer-finder section.
 */
(function () {
  'use strict';

  if (window.InkwellPrinter) {
    if (
      document.readyState !== 'loading' ||
      document.querySelector('ink-printer-bar')
    ) {
      window.InkwellPrinter.refresh();
    }
    return;
  }

  var KEY = 'inkwell:printer';
  var EVENT = 'inkwell:printer-change';
  var OPEN_EVENT = 'inkwell:open-printer-finder';
  var STYLE_ID = 'inkwell-printer-context-style';

  // Used when localStorage is unavailable (private mode, blocked cookies).
  var memory = { value: null };
  var storageOk = (function () {
    try {
      var probe = '__ink_probe__';
      window.localStorage.setItem(probe, '1');
      window.localStorage.removeItem(probe);
      return true;
    } catch (e) {
      return false;
    }
  })();

  function sanitize(model) {
    if (!model || typeof model !== 'object') return null;
    var handle = String(model.handle || '').trim();
    if (!handle) return null;
    return {
      handle: handle,
      displayName: String(model.displayName || handle).trim(),
      brand: String(model.brand || '').trim(),
      series: String(model.series || '').trim(),
    };
  }

  function read() {
    var raw;
    if (storageOk) {
      try {
        raw = window.localStorage.getItem(KEY);
      } catch (e) {
        raw = null;
      }
    } else {
      raw = memory.value;
    }
    if (!raw) return null;
    try {
      return sanitize(JSON.parse(raw));
    } catch (e) {
      return null;
    }
  }

  function write(model) {
    var raw = model ? JSON.stringify(model) : null;
    memory.value = raw;
    if (!storageOk) return;
    try {
      if (raw) window.localStorage.setItem(KEY, raw);
      else window.localStorage.removeItem(KEY);
    } catch (e) {
      /* quota or blocked: memory fallback already holds the value */
    }
  }

  function broadcast(model) {
    var event;
    try {
      event = new CustomEvent(EVENT, {
        detail: { printer: model },
        bubbles: true,
        cancelable: false,
      });
    } catch (e) {
      event = document.createEvent('CustomEvent');
      event.initCustomEvent(EVENT, true, false, { printer: model });
    }
    document.dispatchEvent(event);
  }

  /* ---------------------------------------------------------------- styles */

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = [
      'ink-printer-bar{display:block;box-sizing:border-box;block-size: 44px;width:100%;',
      'background:var(--ink-cream,#F7F4EF);',
      'box-shadow:inset 0 -1px 0 var(--ink-line,#E6E6E6);',
      'color:var(--ink-black,#1A1817);border-radius:0;}',
      'ink-printer-bar[hidden]{display:none;}',
      // Height is locked at 44px because
      // the <head> placeholder in layout/theme.liquid reserves exactly that much
      // before the bar exists. Nothing here may wrap, or the handover shifts.
      '.ink-printer-bar__inner{display:flex;align-items:center;gap:8px;flex-wrap:nowrap;',
      'box-sizing:border-box;block-size: 44px;width:100%;max-width:var(--ink-container,1200px);',
      'margin-inline:auto;padding:0 20px;}',
      '.ink-printer-bar__label{flex:0 0 auto;font-size:12px;font-weight:700;letter-spacing:.08em;',
      'text-transform:uppercase;opacity:.7;white-space:nowrap;}',
      '.ink-printer-bar__name{flex:0 1 auto;min-width:0;font-size:14px;font-weight:700;',
      'line-height:1.3;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}',
      '.ink-printer-bar__clear{position:relative;display:inline-flex;align-items:center;justify-content:center;',
      'flex:0 0 auto;width:44px;height:44px;margin-inline-start:auto;padding:0;border:0;border-radius:0;',
      'background:none;color:inherit;cursor:pointer;line-height:0;}',
      '.ink-printer-bar__clear::before{content:"";position:absolute;inset:5px;background:transparent;}',
      '.ink-printer-bar__clear:hover::before{background:var(--ink-line,#E6E6E6);}',
      '.ink-printer-bar__clear svg{position:relative;z-index:1;pointer-events:none;}',
      '.ink-printer-bar__clear:focus-visible{outline:2px solid var(--ink-blue,#1B3A5C);',
      'outline-offset:-5px;}',
      '@media screen and (max-width:749px){.ink-printer-bar__inner{padding-inline:16px;}',
      '.ink-printer-bar__label{font-size:11px;}}',
    ].join('');
    (document.head || document.documentElement).appendChild(style);
  }

  /* ------------------------------------------------------------------- bar */

  var bar = null;

  function normalizeBar(node) {
    if (!node) return null;
    injectStyles();
    bar = node;
    bar.setAttribute('data-ink-printer-bar', '');
    wireBar();
    return bar;
  }

  function existingBar() {
    if (bar && bar.isConnected) return normalizeBar(bar);
    var existing = document.querySelector('ink-printer-bar');
    if (existing) return normalizeBar(existing);
    bar = null;
    return null;
  }

  function mountPoint() {
    var headerGroup = document.getElementById('header-group');
    if (headerGroup && headerGroup.parentNode) {
      return { parent: headerGroup.parentNode, before: headerGroup.nextSibling };
    }
    var wrapper = document.querySelector('.page-wrapper');
    if (wrapper) return { parent: wrapper, before: wrapper.firstChild };
    if (document.body) return { parent: document.body, before: document.body.firstChild };
    return null;
  }

  function buildBar() {
    var existing = existingBar();
    if (existing) return existing;

    var target = mountPoint();
    if (!target) return null;

    bar = document.createElement('ink-printer-bar');
    bar.setAttribute('role', 'region');
    bar.setAttribute('aria-label', 'Selected printer');
    bar.hidden = true;
    bar.innerHTML =
      '<div class="ink-printer-bar__inner">' +
      '<span class="ink-printer-bar__label">Shopping for:</span>' +
      '<span class="ink-printer-bar__name" data-ink-name aria-live="polite"></span>' +
      '<button type="button" class="ink-printer-bar__clear" data-ink-clear aria-label="Clear selected printer">' +
      '<svg viewBox="0 0 14 14" width="12" height="12" fill="none" aria-hidden="true" focusable="false">' +
      '<path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1.6"/></svg>' +
      '</button>' +
      '</div>';

    target.parent.insertBefore(bar, target.before);
    return normalizeBar(bar);
  }

  function wireBar() {
    if (!bar) return;
    var button = bar.querySelector('[data-ink-clear]');
    if (!button || button._inkWired) return;
    button._inkWired = true;
    bar._inkWired = true;
    button.addEventListener('click', function () {
      api.clear();
    });
  }

  /**
   * The <head> in layout/theme.liquid reads localStorage synchronously and, when
   * a printer is stored, adds `ink-has-printer` plus a 44px placeholder under
   * #header-group so the first paint already has the final geometry. Everything
   * below runs in one synchronous task, so the browser never gets a frame where
   * both the placeholder and the real bar are in the layout.
   */
  function syncDocumentState(model) {
    var root = document.documentElement;
    if (!root) return;
    root.classList.toggle('ink-has-printer', !!model);
    // Hands the reserved 44px over to the real bar (or reclaims it when there is
    // no printer after all, e.g. corrupted storage the head script let through).
    root.classList.add('ink-printer-ready');
  }

  function renderBar() {
    var model = read();
    syncDocumentState(model);
    var node = existingBar();
    if (!model) {
      if (node) node.hidden = true;
      return;
    }
    if (!node) node = buildBar();
    if (!node) return;
    var name = node.querySelector('[data-ink-name]');
    if (name) name.textContent = model.displayName || model.handle;
    var button = node.querySelector('[data-ink-clear]');
    if (button) {
      button.setAttribute(
        'aria-label',
        'Stop shopping for ' + (model.displayName || model.handle)
      );
    }
    node.hidden = false;
  }

  /* --------------------------------------------------------- open finder */

  function isVisible(el) {
    if (!el || !el.isConnected) return false;
    if (el.getClientRects().length === 0) return false;
    var style = window.getComputedStyle(el);
    return style.visibility !== 'hidden' && style.display !== 'none';
  }

  function findFinder() {
    var nodes = document.querySelectorAll('printer-finder');
    for (var i = 0; i < nodes.length; i++) {
      if (isVisible(nodes[i])) return nodes[i];
    }
    return null;
  }

  /**
   * Scroll the first visible printer-finder into view and focus its first
   * usable control. Returns false when the page has no visible finder, so the
   * caller can fall back to navigating somewhere that does (e.g. /#printer-finder).
   */
  function openFinder() {
    var finder = findFinder();
    if (!finder) return false;

    var reduceMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    try {
      finder.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    } catch (e) {
      finder.scrollIntoView();
    }

    var controls = finder.querySelectorAll('select, input');
    for (var i = 0; i < controls.length; i++) {
      if (controls[i].disabled || !isVisible(controls[i])) continue;
      try {
        // preventScroll so focus does not fight the smooth scroll above.
        controls[i].focus({ preventScroll: true });
      } catch (e) {
        controls[i].focus();
      }
      break;
    }
    return true;
  }

  // Agent E's PDP fit badge (and anything else) dispatches a cancelable event
  // rather than reaching into this module directly.
  document.addEventListener(OPEN_EVENT, function (event) {
    if (!openFinder()) return;
    if (event.cancelable) event.preventDefault();
  });

  function whenReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  /* ------------------------------------------ slideshow accessibility */

  function syncSlideshowInert(root) {
    if (!root) return;
    var slides = [];
    if (
      root.nodeType === 1 &&
      root.matches &&
      root.matches('slideshow-slide[aria-hidden]')
    ) {
      slides.push(root);
    }
    if (root.querySelectorAll) {
      var descendants = root.querySelectorAll('slideshow-slide[aria-hidden]');
      for (var i = 0; i < descendants.length; i++) slides.push(descendants[i]);
    }
    for (var j = 0; j < slides.length; j++) {
      slides[j].toggleAttribute(
        'inert',
        slides[j].getAttribute('aria-hidden') === 'true'
      );
    }
  }

  function watchSlideshowInert() {
    syncSlideshowInert(document);
    if (!document.body || typeof MutationObserver === 'undefined') return;
    var observer = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var record = records[i];
        if (record.type === 'attributes') {
          syncSlideshowInert(record.target);
          continue;
        }
        for (var j = 0; j < record.addedNodes.length; j++) {
          syncSlideshowInert(record.addedNodes[j]);
        }
      }
    });
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-hidden'],
    });
  }

  /* ------------------------------------------------------------------- api */

  var api = {
    KEY: KEY,
    EVENT: EVENT,
    OPEN_EVENT: OPEN_EVENT,

    /** Reveal the finder on this page. False means there is none to reveal. */
    open: function () {
      return openFinder();
    },

    get: function () {
      return read();
    },

    set: function (model) {
      var clean = sanitize(model);
      if (!clean) return null;
      write(clean);
      renderBar();
      broadcast(clean);
      return clean;
    },

    clear: function () {
      write(null);
      renderBar();
      broadcast(null);
    },

    /** Re-inject the bar, e.g. after the theme editor swaps the header group. */
    refresh: function () {
      renderBar();
    },
  };

  window.InkwellPrinter = api;

  whenReady(function () {
    renderBar();
    watchSlideshowInert();
  });

  // Keep tabs in sync.
  window.addEventListener('storage', function (event) {
    if (event.key !== KEY) return;
    renderBar();
    broadcast(read());
  });

  // Theme editor re-renders sections; the bar can get detached with the header group.
  document.addEventListener('shopify:section:load', function () {
    if (bar && !bar.isConnected) bar = null;
    renderBar();
  });
})();
